import type { CSSProperties } from 'react'

import {
  ITextMarkupSpec,
  TEXT_PADDING_PX,
  textMarkupBoxStyle,
  textMarkupFont,
} from './markupTextStyle'

/** A run drawn with one fillText at its measured left. */
export interface ITextSegment {
  text: string
  left: number
}

export interface ITextLine {
  text: string
  left: number
  right: number
  baseline: number
  segments: ITextSegment[]
}

/** CSS px at scale 1, padding included: exactly the saved-view box. */
export interface ITextLayout {
  width: number
  height: number
  lines: ITextLine[]
}

/** One grapheme's box relative to the text box, or a hard line break. */
export type MeasuredGrapheme =
  | { text: string; left: number; right: number; top: number; bottom: number }
  | '\n'

interface ILineMetrics {
  // Height of one line box, and the baseline's offset from its top.
  lineHeight: number
  baseline: number
}

const EMOJI = /\p{Extended_Pictographic}/u
// Graphemes closer than this continue the same run.
const CONTIGUOUS_PX = 1

// Lines from measured graphemes: a grapheme starting below the current line's bottom
// starts a new line (soft wrap), '\n' ends one. Every line box has the same height,
// so line k's baseline sits k line boxes below the first.
export function groupTextLines(
  graphemes: MeasuredGrapheme[],
  metrics: ILineMetrics,
  padding: number,
): ITextLine[] {
  const lines: ITextLine[] = []
  let cur:
    | (ITextLine & {
        bottom: number
        lastLeft: number
        lastRight: number
        lastEmoji: boolean
      })
    | null = null
  let index = 0
  const baselineOf = (k: number): number =>
    padding + k * metrics.lineHeight + metrics.baseline
  const flush = (): void => {
    if (!cur) return
    lines.push({
      text: cur.text,
      left: cur.left,
      right: cur.right,
      baseline: cur.baseline,
      segments: cur.segments,
    })
    cur = null
    index++
  }
  for (const g of graphemes) {
    if (g === '\n') {
      if (cur) flush()
      else index++
      continue
    }
    if (cur && g.top >= cur.bottom - 0.5) flush()
    const emoji = EMOJI.test(g.text)
    if (!cur) {
      cur = {
        text: '',
        left: g.left,
        right: g.right,
        baseline: baselineOf(index),
        segments: [],
        bottom: g.bottom,
        lastLeft: g.left,
        lastRight: g.right,
        lastEmoji: false,
      }
    }
    const seg = cur.segments[cur.segments.length - 1]
    const contiguous =
      !!seg &&
      (Math.abs(g.left - cur.lastRight) <= CONTIGUOUS_PX ||
        Math.abs(g.right - cur.lastLeft) <= CONTIGUOUS_PX)
    if (seg && contiguous && !emoji && !cur.lastEmoji) {
      seg.text += g.text
      seg.left = Math.min(seg.left, g.left)
    } else {
      cur.segments.push({ text: g.text, left: g.left })
    }
    cur.text += g.text
    cur.left = Math.min(cur.left, g.left)
    cur.right = Math.max(cur.right, g.right)
    cur.bottom = Math.max(cur.bottom, g.bottom)
    cur.lastLeft = g.left
    cur.lastRight = g.right
    cur.lastEmoji = emoji
  }
  flush()
  return lines
}

interface ISegmenter {
  segment(text: string): Iterable<{ segment: string; index: number }>
}
const SegmenterCtor = (
  Intl as unknown as {
    Segmenter?: new (
      locale: undefined,
      options: { granularity: 'grapheme' },
    ) => ISegmenter
  }
).Segmenter
const graphemeSegmenter = SegmenterCtor
  ? new SegmenterCtor(undefined, { granularity: 'grapheme' })
  : null

function graphemesOf(text: string): { segment: string; index: number }[] {
  if (graphemeSegmenter) return [...graphemeSegmenter.segment(text)]
  let index = 0
  return Array.from(text, segment => {
    const g = { segment, index }
    index += segment.length
    return g
  })
}

const UNITLESS = new Set(['lineHeight', 'fontWeight'])

function applyStyle(el: HTMLElement, css: CSSProperties): void {
  Object.entries(css).forEach(([key, value]) => {
    if (value == null) return
    el.style[key as never] = (
      typeof value === 'number' && !UNITLESS.has(key)
        ? `${value}px`
        : String(value)
    ) as never
  })
}

let host: HTMLDivElement | null = null

// Off-screen, unclipped, and carrying the document's language and direction, so line
// breaking matches the saved view.
function measureHost(): HTMLDivElement {
  if (host?.isConnected) return host
  host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.lang = document.documentElement.lang
  host.dir = document.documentElement.dir
  applyStyle(host, {
    position: 'fixed',
    left: -99999,
    top: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    contain: 'layout style',
  })
  document.body.appendChild(host)
  return host
}

const metricsByFont = new Map<string, ILineMetrics>()

function boxOf(spec: ITextMarkupSpec): HTMLDivElement {
  const el = document.createElement('div')
  applyStyle(el, {
    ...textMarkupBoxStyle(spec),
    padding: TEXT_PADDING_PX,
    position: 'absolute',
  })
  return el
}

// Lays the specs out in the browser exactly as the saved view does: every node is
// written, then all are read in one pass, then removed.
export function layoutTextMarkups(specs: ITextMarkupSpec[]): ITextLayout[] {
  const root = measureHost()
  const nodes = specs.map(spec => {
    const el = boxOf(spec)
    el.textContent = spec.text
    root.appendChild(el)
    return el
  })
  const probes = new Map<
    string,
    { box: HTMLDivElement; mark: HTMLSpanElement }
  >()
  specs.forEach(spec => {
    const font = textMarkupFont(spec)
    if (metricsByFont.has(font) || probes.has(font)) return
    const box = boxOf({ ...spec, text: 'x', boxWidth: null })
    box.style.padding = '0'
    const mark = document.createElement('span')
    applyStyle(mark, { display: 'inline-block', width: 0, height: 0 })
    box.append('x', mark)
    root.appendChild(box)
    probes.set(font, { box, mark })
  })

  probes.forEach(({ box, mark }, font) => {
    const top = box.getBoundingClientRect().top
    metricsByFont.set(font, {
      lineHeight: box.getBoundingClientRect().height,
      baseline: mark.getBoundingClientRect().bottom - top,
    })
  })
  const range = document.createRange()
  const layouts = nodes.map((el, i) => {
    const box = el.getBoundingClientRect()
    const textNode = el.firstChild
    const graphemes: MeasuredGrapheme[] = []
    if (textNode) {
      for (const { segment, index } of graphemesOf(specs[i].text)) {
        if (segment === '\n') {
          graphemes.push('\n')
          continue
        }
        range.setStart(textNode, index)
        range.setEnd(textNode, index + segment.length)
        const r = range.getBoundingClientRect()
        graphemes.push({
          text: segment,
          left: r.left - box.left,
          right: r.right - box.left,
          top: r.top - box.top,
          bottom: r.bottom - box.top,
        })
      }
    }
    return {
      width: box.width,
      height: box.height,
      lines: groupTextLines(
        graphemes,
        metricsByFont.get(textMarkupFont(specs[i])),
        TEXT_PADDING_PX,
      ),
    }
  })

  nodes.forEach(el => el.remove())
  probes.forEach(({ box }) => box.remove())
  return layouts
}
